/**
 * TimePeriodSection Component
 *
 * Renders a section header and 7-column grid for a specific time period.
 * Displays period title and time range with translation support.
 *
 * Requirements: 1.1, 1.2, 10.1, 10.2, 10.3, 10.4
 */

import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'services/Translation'
import { TimeSlot } from '../types'
import { FONTS } from '../utils/theme'
import TimeSlotCell from './TimeSlotCell'
import texts from './Texts'

/**
 * Props for the TimePeriodSection component
 */
export interface TimePeriodSectionProps {
  /** Translation key for the period title (e.g., 'mornings', 'afternoons', 'evenings') */
  title: string
  /** Translation key for the time range (e.g., 'morningTimeRange') */
  timeRange: string
  /** Array of timeslots to display in this period */
  slots: TimeSlot[]
  /** Set of currently selected slot IDs */
  selectedSlotIds: Set<string>
  /** Set of slot IDs highlighted during drag selection */
  highlightedSlotIds: Set<string>
  /** Click handler for individual slots */
  onSlotClick: (slot: TimeSlot, event: React.MouseEvent) => void
}

/**
 * Styled container for the 7-column grid of timeslots
 * Requirement 1.1: Display timeslots in a 7-column responsive grid layout
 */
const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 12px 8px;
  padding: 0 16px;
`

/**
 * Styled header for the time period section
 * Displays the period title and time range with proper styling
 */
const PeriodHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0;
  font-family: ${FONTS.bold};
  color: #1a1a2e;

  .time-range {
    border-left: 2px solid #3b82f6;
    padding-left: 12px;
    margin-left: 16px;
    font-family: ${FONTS.primary};
    color: #1e3a5f;
  }
`

/**
 * TimePeriodSection Component
 *
 * Renders a time period section with a header showing the period title and time range,
 * followed by a 7-column grid of TimeSlotCell components.
 *
 * @param props - Component props
 * @returns React element representing the time period section
 */
const TimePeriodSection: React.FC<TimePeriodSectionProps> = ({
  title,
  timeRange,
  slots,
  selectedSlotIds,
  highlightedSlotIds,
  onSlotClick,
}) => {
  const { t } = useTranslation(texts)

  // Get translated title and time range
  const translatedTitle = t(title) || title
  const translatedTimeRange = t(timeRange) || timeRange

  return (
    <div>
      <PeriodHeader>
        {translatedTitle}
        <span className="time-range">{translatedTimeRange}</span>
      </PeriodHeader>
      <GridContainer>
        {slots.map((slot) => (
          <TimeSlotCell
            key={slot.id}
            slot={slot}
            isSelected={selectedSlotIds.has(slot.id)}
            isHighlighted={highlightedSlotIds.has(slot.id)}
            onClick={(event) => onSlotClick(slot, event)}
          />
        ))}
      </GridContainer>
    </div>
  )
}

export default React.memo(TimePeriodSection)
